import React, { useState, useEffect } from 'react';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal } from '@bijie/react-bnb';
import dynamic from 'next/dynamic';
import Container from '../Container';
import Spinner from '../Spinner';
import { TEXT_CONFIG } from './enum';
import { StoreContext } from './context';
import { fetchNextStep } from '../../actions/actionsSet/reset2fa';

import { getLabel } from '../../utils/i18n';

const {
  publicRuntimeConfig: {
    assetPrefix,
  },
} = getConfig();

const Question = dynamic(import('./Question'), {
  ssr: false,
  loading: () => null,
});
const IdVerification = dynamic(import('./IdVerification'), {
  ssr: false,
  loading: () => null,
});
const Face = dynamic(import('./Face'), {
  ssr: false,
  loading: () => null,
});
const Review = dynamic(import('./Review'), {
  ssr: false,
  loading: () => null,
});

const FLOW_MAP = {
  LOADING: Spinner,
  QUESTION: Question,
  UPLOAD: IdVerification,
  FACE: Face,
  REVIEW: Review,
};

const Wrapper = styled.div`
  margin: 100px 0 200px 0;
  color: #212833;
  font-size: 14px;
  .center {text-align: center;}
  .title {
    padding-bottom: 40px;
    line-height: 44px;
    text-align: center;
    font-size: 34px;
    font-family: ${({ theme }) => theme.fontIBMLight};
  }
  .box {
    position: relative;
    min-height: 300px;
    padding: 60px 40px 50px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.06);
    .sub-title {
      margin-bottom: 30px;
      line-height: 32px;
      font-size: 24px;
      font-family: ${({ theme }) => theme.fontIBMMedium};
    }
  }
  .email-verify { margin-top: -40px; }
  .bnc-btn {width: 260px;}
`;

export const FlowBox = ({
  i18n, type, children, providerValue,
}) => (
  <Container>
    <Wrapper>
      <div className="title">{getLabel(TEXT_CONFIG[type].title, i18n)}</div>
      <div className="box">
        <StoreContext.Provider value={providerValue}>
          {children}
        </StoreContext.Provider>
      </div>
    </Wrapper>
  </Container>
);
FlowBox.propTypes = {
  i18n: PropTypes.object,
  type: PropTypes.string,
  children: PropTypes.node,
  providerValue: PropTypes.object,
};

const DEFAULT_STEP = {
  name: 'LOADING',
  config: {
    requestId: '000000',
  },
};
const Flow = ({ i18n, locale, query }) => {
  const [{ name, config }, setCurStep] = useState(DEFAULT_STEP);
  const { type = 'google' } = query;
  const fetchData = async () => {
    setCurStep(DEFAULT_STEP);
    try {
      const { nextStep, ...params } = await fetchNextStep({
        type,
      });
      setCurStep({
        name: nextStep,
        config: params,
      });
    } catch ({ message }) {
      message && Modal.info({
        icon: <img src={assetPrefix + require('../../static/images/2fa/error.svg')} width="78" alt="error" />,
        content: <p>{message}</p>,
        okText: getLabel('exchange-modal-iUnderstood', i18n),
      });
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const CurFlow = FLOW_MAP[name];
  const providerValue = {
    i18n,
    locale,
    config,
    type,
    gotoNextStep: fetchData,
  };
  return (
    <FlowBox type={type} i18n={i18n} providerValue={providerValue}>
      <CurFlow key={`${name}_${config.requestId}`} config={config} />
    </FlowBox>
  );
};

Flow.propTypes = {
  i18n: PropTypes.object,
  locale: PropTypes.string,
  query: PropTypes.object,
};

const memoized = React.memo(Flow);

export default memoized;
